import React from "react";
import { Helmet } from "react-helmet-async";
import { ChevronDown, MessageSquare } from "lucide-react";
import { Link } from "react-router-dom";

const FaqElectronique = ({
  setOpenIndex,
  openIndex,
  toggleFAQ,
  idCategorie,
}) => {
  const faqList = [
    {
      question: () => (
        <strong>
          1. Pourquoi utiliser Deal.mg pour acheter de l'électronique ?
        </strong>
      ),
      answer: () => (
        <p>
          Deal.mg offre un environnement sécurisé et une communauté active.
          Acheter d'occasion vous permet de trouver du matériel tech de qualité
          à un prix bien inférieur au neuf.
        </p>
      ),
    },
    {
      question: () => (
        <strong>
          2. Comment assurer la sécurité lors de l'achat de matériel
          électronique ?
        </strong>
      ),
      answer: () => (
        <p>
          Pour acheter un{" "}
          <strong>smartphone ou un ordinateur d'occasion</strong> en toute
          sécurité sur Deal.mg, demandez systématiquement une vidéo de
          l'appareil en marche et une capture d'écran des paramètres système via
          téléphone. Pour les envois en province, exigez une photo du colis et
          du bordereau de transport avant de finaliser le paiement. Privilégiez
          les vendeurs qui fournissent des descriptions détaillées et qui
          acceptent de répondre précisément à vos questions techniques.
        </p>
      ),
    },
    {
      question: () => (
        <strong>
          3. Comment vendre mes appareils électroniques rapidement ?
        </strong>
      ),
      answer: () => (
        <p>
          Prenez des photos claires de l'appareil allumé et rédigez une
          description précise de son état, de sa capacité et des accessoires
          inclus. L'utilisation de notre outil d'IA aide à générer une
          description optimisée pour le <strong>SEO électronique</strong>.
        </p>
      ),
    },
    {
      question: () => (
        <strong>
          4. Puis-je trouver des iPhones ou Samsung récents en occasion ?
        </strong>
      ),
      answer: () => (
        <p>
          Oui, de nombreux utilisateurs revendent leurs smartphones récents.
          Utilisez la barre de recherche pour trouver des modèles spécifiques et
          vérifiez bien l'état de la batterie et l'absence de blocage opérateur.
        </p>
      ),
    },
    {
      question: () => (
        <strong>5. Comment vérifier l'état d'un ordinateur d'occasion ?</strong>
      ),
      answer: () => (
        <p>
          Demandez à tester l'ordinateur : vérifiez la vitesse de démarrage,
          l'état de l'écran, le fonctionnement des ports USB et la durée de vie
          de la batterie. Demandez si le système d'exploitation est
          réinitialisé.
        </p>
      ),
    },
    {
      question: () => (
        <strong>
          6. Comment fixer le juste prix pour mon matériel tech à vendre ?
        </strong>
      ),
      answer: () => (
        <p>
          Comparez votre appareil avec des modèles similaires publiés sur
          Deal.mg, en tenant compte de son âge, de son état général et de la
          demande sur le marché malgache.
        </p>
      ),
    },
    {
      question: () => (
        <strong>
          7. Existe-t-il des risques de contrefaçon pour les smartphones ?
        </strong>
      ),
      answer: () => (
        <p>
          La vigilance est de mise. Pour les smartphones de marque (iPhone,
          Samsung), demandez la facture d'origine ou vérifiez le numéro IMEI
          pour confirmer l'authenticité de l'appareil.
        </p>
      ),
    },
    {
      question: () => (
        <strong>8. Comment signaler un appareil électronique suspect ?</strong>
      ),
      answer: () => (
        <p>
          Si vous détectez une annonce frauduleuse (prix trop bas, photos
          volées) ou un appareil contrefait, cliquez sur le bouton « Signaler »
          pour alerter notre équipe de modération.
        </p>
      ),
    },
    {
      question: () => (
        <strong>9. Comment vérifier l'authenticité d'un smartphone ?</strong>
      ),
      answer: () => (
        <p>
          Demandez la facture d'origine ou vérifiez le numéro IMEI pour
          confirmer que l'appareil n'est pas une contrefaçon ou un produit volé.
        </p>
      ),
    },
    {
      question: () => (
        <strong>
          10. Comment Deal.mg protège-t-il mes transactions d'appareils tech ?
        </strong>
      ),
      answer: () => (
        <p>
          Deal.mg facilite la mise en relation sécurisée. Pour l'électronique,
          nous recommandons{" "}
          <strong>
            d'essayer l'appareil (allumage, batterie, fonctionnalités)
          </strong>{" "}
          en présence du vendeur avant de finaliser le paiement pour éviter tout
          vice caché.
        </p>
      ),
    },
  ];

  return (
    <>
      <Helmet>
        <title>Électronique & High-Tech à Madagascar | Deal.mg</title>
        <meta
          name="description"
          content="Découvrez la meilleure sélection d'électronique et high-tech d'occasion à Madagascar sur Deal.mg : smartphones, ordinateurs, téléviseurs."
        />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org/",
            "@type": "Product",
            name: "Électronique et High-Tech à Madagascar",
            image: ["https://deal.mg/logo.png"],
            description:
              "Smartphones, ordinateurs et téléviseurs d'occasion à Madagascar sur Deal.mg",
            sku: idCategorie,
            offers: {
              "@type": "Offer",
              url: `https://deal.mg/article/resultat?idCategorie=${idCategorie}`,
              availability: "https://schema.org/InStock",
            },
          })}
        </script>
      </Helmet>
      <h1 className="text-center">
        Électronique et high-tech, le meilleur de l'occasion
      </h1>
      <p>
        Équipez-vous avec du <strong>matériel électronique</strong> occasion
        performant sans vous ruiner. Deal.mg propose une vaste sélection de{" "}
        <strong>smartphones occasion</strong>, ordinateurs portables et
        téléviseurs. Achetez malin en comparant les offres de notre communauté
        active et sécurisez vos transactions en choisissant des vendeurs
        vérifiés. Donnez une seconde vie à vos appareils tech en publiant une
        annonce optimisée par notre outil d'IA.
      </p>
      <h2>Smartphones et tablettes</h2>
      <p>
        Mettez à jour votre équipement sans vous ruiner. Explorez notre large
        sélection de
        {" "}<strong>smartphones occasion</strong> : iPhone, Samsung, Huawei et bien
        d'autres. Trouvez également des tablettes pour le travail ou le
        divertissement, avec des annonces vérifiées pour éviter les
        contrefaçons.
      </p>
      <h2>Informatique et bureautique</h2>
      <p>
        Équipez votre bureau avec du{" "}
        <strong>matériel informatique occasion</strong> performant. Découvrez
        des ordinateurs portables, PC de bureau, écrans et imprimantes adaptés à
        vos besoins professionnels ou personnels. Le télétravail à Madagascar
        devient plus accessible grâce aux bonnes affaires de notre communauté.
        C'est aussi l'occasion idéale pour trouver des {" "}
          <strong>bureaux professionnels </strong>
        ou des{" "}
          <strong>fournitures de bureau</strong>
        pour optimiser votre espace de travail.
      </p>
      <h2>Télévision, son et photo</h2>
      <p>
        Plongez dans l'univers du multimédia. Trouvez des téléviseurs LED, des
        systèmes sonores, des appareils photo reflex ou hybrides, et des
        consoles de jeux. Les meilleures opportunités pour vos{" "}
        <strong>loisirs électroniques à Madagascar</strong> sont sur Deal.mg.
      </p>
      <h2>Valorisez votre matériel au meilleur prix</h2>
      <p>
        Vous souhaitez renouveler votre matériel ? Transformez vos anciens
        appareils en argent. Publiez une annonce détaillée en utilisant notre
        outil d'IA pour optimiser votre description et garantir une{" "}
        <strong>vente rapide</strong>.
      </p>
      <br />
      <h2 className="support_faq_title">
        <MessageSquare size={20} className="support_faq_title_icon" />
        FAQ
      </h2>
      {faqList.map((item, index) => (
        <div
          key={index}
          className={`support_faq_item ${openIndex === index ? "open" : ""}`}
        >
          <div
            className="support_faq_question"
            onClick={() => toggleFAQ(index)}
          >
            <span>{item.question()}</span>
            <ChevronDown
              className={`support_faq_arrow ${
                openIndex === index ? "support_faq_rotate_180" : ""
              }`}
              size={20}
            />
          </div>

          <div
            className={`support_faq_answer_container${openIndex === index ? "active" : ""}`}
          >
            <div className="support_faq_answer">{item.answer()}</div>
          </div>
        </div>
      ))}
    </>
  );
};

export default FaqElectronique;
